/*
* RMKM Scheduler IEP
* added by @jaseem 
* date : 20 Nov 2025
* IEP Scheduler List  */

import React, { useEffect, useState } from "react";
import { Button, Col, Row, Table, Modal, Form, Alert } from "react-bootstrap";
import inventoryApi from "../api/inventoryApi";
import authApi from "../api/authApi";
import moment from "moment";
import Select from "react-select";
import * as constants from "../constants/CONSTANT";

import {
  DatatableWrapper,
  Filter,
  Pagination,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import { Link } from "react-router-dom";

const SchedulerIEPList = () => {
  const [body, setBody] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedScheduler, setSelectedScheduler] = useState(null);
  const [users, setUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [usersWithoutIEP, setUsersWithoutIEP] = useState([]);
  const [selectedSchedulerForUsers, setSelectedSchedulerForUsers] = useState(null);
  
  // Field-specific validation errors
  const [fieldErrors, setFieldErrors] = useState({
    start_date: "",
    end_date: "",
    targeted_audience: "",
    userid: "",
  });

  const [formData, setFormData] = useState({
    start_date: "",
    end_date: "",
    targeted_audience: "all",
    userid: [],
  });

  useEffect(() => {
    fetchAllSchedulers();
    fetchUsers();
  }, []);

  const fetchAllSchedulers = async () => {
    try {
      const result = await inventoryApi.fetchAllSchedulers();
      if (result && Array.isArray(result)) {
        const formattedData = result.map((item) => ({
          ...item,
          start_date_formatted: moment(item.start_date).format("DD-MM-YYYY"),
          end_date_formatted: moment(item.end_date).format("DD-MM-YYYY"),
          targeted_audience_display: item.targeted_audience === "all" ? "All Users" : "Specific Users",
          userid_display: Array.isArray(item.userid) && item.userid.length > 0 
            ? item.userid.length + " user(s)" 
            : "All Staff Members",
        }));
        setBody(formattedData);
      } else {
        setBody([]);
      }
    } catch (error) {
      console.error("Error fetching schedulers:", error);
      setBody([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        constants.API_BASE_URL + "/api/auth/users",
        {
          method: "GET",
          mode: "cors",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        }
      );
      if (response.status === 401) {
        authApi.logout();
      }
      const result = await response.json();
      if (result && Array.isArray(result)) {
        setUsers(result);
        const options = result.map((user) => ({
          value: user.id,
          label: `${user.firstname || ''} ${user.lastname || ''} (${user.email || user.username || ''})`,
        }));
        setUserOptions(options);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const header = [
    {
      title: "Start Date",
      prop: "start_date_formatted",
      isFilterable: true,
    },
    {
      title: "End Date",
      prop: "end_date_formatted",
      isFilterable: true,
    },
    {
      title: "Targeted Audience",
      prop: "targeted_audience_display",
      isFilterable: true,
    },
    {
      title: "Users",
      prop: "userid_display",
      isFilterable: false,
    },
    {
      title: "Status",
      prop: "status",
      isFilterable: true,
      cell: (row) => {
        const today = moment().format("YYYY-MM-DD");
        const startDate = moment(row.start_date).format("YYYY-MM-DD");
        const endDate = moment(row.end_date).format("YYYY-MM-DD");
        
        if (today >= startDate && today <= endDate) {
          return <span className="badge bg-success">Active</span>;
        } else if (today < startDate) {
          return <span className="badge bg-info">Upcoming</span>;
        } else {
          return <span className="badge bg-secondary">Expired</span>;
        }
      },
    },
    {
      title: "Actions",
      prop: "id",
      cell: (row) => {
        const today = moment().format("YYYY-MM-DD");
        const endDate = moment(row.end_date).format("YYYY-MM-DD");
        const isExpired = today > endDate;
        
        return (
          <>
            <Button
              className="btn-sm mx-1"
              variant="outline-primary"
              onClick={() => handleEdit(row)}
              title="Edit"
            >
              <i className="fa-regular fa-pen-to-square"></i>
            </Button>
            {isExpired && (
              <Button
                className="btn-sm mx-1"
                variant="outline-info"
                onClick={() => handleViewUsersWithoutIEP(row)}
                title="View Users Without IEP"
              >
                <i className="fa-solid fa-users"></i>
              </Button>
            )}
            <Button
              className="btn-sm mx-1"
              variant="outline-danger"
              onClick={() => handleDeleteClick(row)}
              title="Delete"
            >
              <i className="fa fa-trash"></i>
            </Button>
          </>
        );
      },
    },
  ];

  const handleCreate = () => {
    setSelectedScheduler(null);
    setFormData({
      start_date: "",
      end_date: "",
      targeted_audience: "all",
      userid: [],
    });
    setFieldErrors({
      start_date: "",
      end_date: "",
      targeted_audience: "",
      userid: "",
    });
    setShowModal(true);
    setShowError(false);
    setErrorMessage("");
  };

  const handleEdit = (scheduler) => {
    setSelectedScheduler(scheduler);
    setFormData({
      start_date: scheduler.start_date ? moment(scheduler.start_date).format("YYYY-MM-DD") : "",
      end_date: scheduler.end_date ? moment(scheduler.end_date).format("YYYY-MM-DD") : "",
      targeted_audience: scheduler.targeted_audience || "all",
      userid: Array.isArray(scheduler.userid) ? scheduler.userid : [],
    });
    setFieldErrors({
      start_date: "",
      end_date: "",
      targeted_audience: "",
      userid: "",
    });
    setShowModal(true);
    setShowError(false);
    setErrorMessage("");
  };

  const handleDeleteClick = (scheduler) => {
    setSelectedScheduler(scheduler);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      const result = await inventoryApi.deleteScheduler(selectedScheduler.id);
      if (result && result.success) {
        setShowDeleteModal(false);
        fetchAllSchedulers();
      } else {
        setErrorMessage(result?.message || "Failed to delete scheduler");
        setShowError(true);
      }
    } catch (error) {
      setErrorMessage("Error deleting scheduler");
      setShowError(true);
    }
  };

  const handleViewUsersWithoutIEP = async (scheduler) => {
    try {
      setSelectedSchedulerForUsers(scheduler);
      const result = await inventoryApi.getUsersWithoutIEP(scheduler.id);
      if (result && Array.isArray(result)) {
        setUsersWithoutIEP(result);
        setShowUsersModal(true);
      } else {
        setUsersWithoutIEP([]);
        setShowUsersModal(true);
      }
    } catch (error) {
      console.error("Error fetching users without IEP:", error);
      setErrorMessage("Error fetching users without IEP");
      setShowError(true);
    }
  };

  const handleCreateSchedulerForUsers = (userIds) => {
    // Pre-fill form with selected users
    setFormData({
      start_date: "",
      end_date: "",
      targeted_audience: "Specific",
      userid: userIds,
    });
    setShowUsersModal(false);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowError(false);
    setErrorMessage("");
    
    // Reset field errors
    const newFieldErrors = {
      start_date: "",
      end_date: "",
      targeted_audience: "",
      userid: "",
    };
    
    let hasErrors = false;

    // Validate Start Date
    if (!formData.start_date || formData.start_date.trim() === "") {
      newFieldErrors.start_date = "Please provide start date";
      hasErrors = true;
    }

    // Validate End Date
    if (!formData.end_date || formData.end_date.trim() === "") {
      newFieldErrors.end_date = "Please provide end date";
      hasErrors = true;
    }

    // Validate date range
    if (formData.start_date && formData.end_date) {
      if (moment(formData.start_date).isAfter(moment(formData.end_date))) {
        newFieldErrors.end_date = "End date must be after start date";
        hasErrors = true;
      }
    }

    // Validate Targeted Audience
    if (!formData.targeted_audience || formData.targeted_audience.trim() === "") {
      newFieldErrors.targeted_audience = "Please select targeted audience";
      hasErrors = true;
    }

    // Validate Users for Specific audience
    if (formData.targeted_audience === "Specific" && (!formData.userid || formData.userid.length === 0)) {
      newFieldErrors.userid = "Please select at least one user";
      hasErrors = true;
    }

    setFieldErrors(newFieldErrors);

    if (hasErrors) {
      return;
    }

    try {
      const schedulerData = {
        start_date: formData.start_date,
        end_date: formData.end_date,
        targeted_audience: formData.targeted_audience,
        userid: formData.targeted_audience === "all" ? [] : formData.userid,
      };

      let result;
      if (selectedScheduler) {
        result = await inventoryApi.updateScheduler(selectedScheduler.id, schedulerData);
      } else {
        result = await inventoryApi.createScheduler(schedulerData);
      }

      if (result && (result.id || result.success)) {
        setShowModal(false);
        fetchAllSchedulers();
      } else {
        setErrorMessage(result?.errors || result?.message || "Failed to save scheduler");
        setShowError(true);
      }
    } catch (error) {
      setErrorMessage("Error saving scheduler");
      setShowError(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: "",
      });
    }
  };

  const handleUserSelection = (selectedOptions) => {
    const userIds = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
    setFormData({
      ...formData,
      userid: userIds,
    });
    // Clear error when user selects
    if (fieldErrors.userid) {
      setFieldErrors({
        ...fieldErrors,
        userid: "",
      });
    }
  };

  const getSelectedUserOptions = () => {
    if (!formData.userid || formData.userid.length === 0) return [];
    return userOptions.filter((opt) => formData.userid.includes(opt.value));
  };

  return (
    <Row className="g-0">
      <Col lg={2} className="mx-2">
        <Link className="nav-link">
          Staff Member <i className="fa-solid fa-chevron-right"></i> IEP Scheduler
        </Link>
      </Col>

      <Col lg={12} className="p-lg-4">
        {localStorage.getItem("userrole") !== "SUPER_ADMIN" ? (
          <Alert variant="warning">Only Super Admin can access this page.</Alert>
        ) : (
          <>
            <Row className="mb-4">
              <Col lg={10}>
                <h4>IEP Scheduler Management</h4>
              </Col>
              <Col lg={2} className="d-flex justify-content-end">
                <Button variant="primary" onClick={handleCreate}>
                  {/* <i className="fa-solid fa-plus"></i>  */}
                  Create Scheduler
                </Button>
              </Col>
            </Row>

            {body && body.length > 0 ? (
              <DatatableWrapper
                body={body}
                headers={header}
                paginationOptionsProps={{
                  initialState: {
                    rowsPerPage: 10,
                    options: [5, 10, 15, 20],
                  },
                }}
              >
                <Row className="mb-4">
                  <Col
                    xs={12}
                    lg={4}
                    className="d-flex flex-col justify-content-end align-items-end"
                  >
                    <Filter />
                  </Col>
                </Row>
                <Table striped className="data-table">
                  <TableHeader />
                  <TableBody />
                </Table>
                <Pagination />
              </DatatableWrapper>
            ) : (
              <Alert variant="info">No schedulers found. Create one to get started.</Alert>
            )}
          </>
        )}
      </Col>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedScheduler ? "Edit Scheduler" : "Create Scheduler"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} noValidate>
          <Modal.Body>
            {showError && (
              <Alert variant="danger" onClose={() => setShowError(false)} dismissible>
                {errorMessage}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Start Date <i className="text-danger">*</i> </Form.Label>
              <Form.Control
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                isInvalid={!!fieldErrors.start_date}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.start_date}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>End Date <i className="text-danger">*</i> </Form.Label>
              <Form.Control
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                isInvalid={!!fieldErrors.end_date}
              />
              <Form.Control.Feedback type="invalid">
                {fieldErrors.end_date}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Targeted Audience *</Form.Label>
              <Form.Select
                name="targeted_audience"
                value={formData.targeted_audience}
                onChange={handleInputChange}
                isInvalid={!!fieldErrors.targeted_audience}
              >
                <option value="">Select Targeted Audience</option>
                <option value="all">All Users</option>
                <option value="Specific">Specific Users</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {fieldErrors.targeted_audience}
              </Form.Control.Feedback>
            </Form.Group>

            {formData.targeted_audience === "Specific" && (
              <Form.Group className="mb-3">
                <Form.Label>Select Users *</Form.Label>
                <div className={fieldErrors.userid ? "is-invalid" : ""}>
                  <Select
                    isMulti
                    name="userid"
                    options={userOptions}
                    value={getSelectedUserOptions()}
                    onChange={handleUserSelection}
                    placeholder="Select users..."
                    className={fieldErrors.userid ? "is-invalid" : ""}
                  />
                </div>
                {fieldErrors.userid && (
                  <div className="invalid-feedback d-block">
                    {fieldErrors.userid}
                  </div>
                )}
                <Form.Text className="text-muted">
                  Select one or more users who should see the Create IEP button
                </Form.Text>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedScheduler ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this scheduler? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Users Without IEP Modal */}
      <Modal show={showUsersModal} onHide={() => setShowUsersModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Users Who Haven't Created IEP
            {selectedSchedulerForUsers && (
              <small className="text-muted d-block mt-1">
                Scheduler: {moment(selectedSchedulerForUsers.start_date).format("DD-MM-YYYY")} to {moment(selectedSchedulerForUsers.end_date).format("DD-MM-YYYY")}
              </small>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {usersWithoutIEP && usersWithoutIEP.length > 0 ? (
            <>
              <Alert variant="info">
                These users were targeted in the scheduler but haven't created an IEP yet. 
                You can create a new scheduler for them with extended dates.
              </Alert>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {usersWithoutIEP.map((user) => (
                    <tr key={user.id}>
                      <td>{user.username || `${user.firstname} ${user.lastname}`}</td>
                      <td>{user.email}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleCreateSchedulerForUsers([user.id])}
                        >
                          Create Scheduler
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="mt-3">
                <Button
                  variant="primary"
                  onClick={() => handleCreateSchedulerForUsers(usersWithoutIEP.map(u => u.id))}
                >
                  Create Scheduler for All Users
                </Button>
              </div>
            </>
          ) : (
            <Alert variant="success">
              All targeted users have created their IEPs! Great job! 🎉
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUsersModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
};

export default SchedulerIEPList;

